import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';

/**
 * AuthModal — shown when a guest tries to upvote or bookmark.
 * Props:
 *   open        boolean
 *   onOpenChange fn(bool)
 *   hint        'vote' | 'bookmark'  (optional, for the headline copy)
 */
export default function AuthModal({ open, onOpenChange, hint }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading]   = useState(false);

  const reset = () => { setEmail(''); setPassword(''); setUsername(''); setMode('login'); };

  const handleClose = (v) => { if (!v) reset(); onOpenChange(v); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success('Welcome back!');
      } else {
        await register(username, email, password);
        toast.success('Account created!');
      }
      handleClose(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || (mode === 'login' ? 'Invalid credentials' : 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  const hintCopy = hint === 'bookmark'
    ? 'Save prompts you love — log in to start bookmarking.'
    : 'Join the community to upvote your favourite prompts.';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-sm" onClick={e => e.stopPropagation()}>
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-primary" />
            <DialogTitle className="text-base">
              {mode === 'login' ? 'Log in to PromptVault' : 'Create an account'}
            </DialogTitle>
          </div>
          <p className="text-sm text-muted-foreground">{hintCopy}</p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3 pt-1">
          {mode === 'register' && (
            <div className="space-y-1.5">
              <Label className="text-xs">Username</Label>
              <Input
                required
                autoFocus
                placeholder="Choose a username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                maxLength={12}
                className="rounded-full h-9 text-sm"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs">Email</Label>
            <Input
              type="email"
              required
              autoFocus={mode === 'login'}
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="rounded-full h-9 text-sm"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Password</Label>
            <Input
              type="password"
              required
              placeholder="••••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="rounded-full h-9 text-sm"
            />
          </div>

          <Button type="submit" disabled={loading} className="w-full rounded-full mt-1 shadow-lg shadow-primary/20">
            {loading ? 'Please wait…' : mode === 'login' ? 'Log in' : 'Create account'}
          </Button>
        </form>

        <div className="text-center pt-1">
          {mode === 'login' ? (
            <p className="text-xs text-muted-foreground">
              No account?{' '}
              <button
                type="button"
                onClick={() => setMode('register')}
                className="text-primary hover:underline font-medium"
              >
                Sign up free
              </button>
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => setMode('login')}
                className="text-primary hover:underline font-medium"
              >
                Log in
              </button>
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
